import { defaultRedactDiagnostic } from "@intrasync/stores";
import type {
  DiagnosticsOptions,
  StoreDiagnosticCode,
  StoreDiagnosticEvent,
  StoreDiagnosticLevel,
} from "@intrasync/stores";
import { env } from "../config/environment";
import { hqDiagnosticsBuffer } from "./diagnosticsBuffer";

/**
 * HQ's DiagnosticsSink policy — the routing half.
 *
 * THE CONTRACT, as it actually is in `@intrasync/stores@26.8.15`
 * (`dist/utils/diagnostics.js`; read, not guessed):
 *
 *   `new DiagnosticsSink({ onEvent?, level?, console?, redact? })`
 *   - every `emit()` builds the event and mirrors `warn`/`error` to the console
 *     when `console !== false`. `debug`/`info` are NEVER console-mirrored —
 *     that is hard-coded, not a level setting.
 *   - `onEvent` is then called only when it exists AND the event's level is
 *     >= `level` (default `"warn"`). So with no sink, `info` reaches nothing at
 *     all and `warn`/`error` reach only the console.
 *   - the event is passed through `redact` (default `defaultRedactDiagnostic`)
 *     BEFORE `onEvent`, never before the console mirror. Doing our own console
 *     rendering from inside `onEvent` therefore prints the REDACTED event —
 *     strictly safer than the built-in mirror, which logs the raw error whose
 *     Feathers payload can carry submitted credentials.
 *   - a throw inside `onEvent` is caught and the event dropped.
 *
 * So HQ sets `level: "info"` (delivery floor) and `console: false` (we render
 * the redacted event ourselves), and applies its own per-level policy below.
 *
 * WHY `info` MATTERS AT BOOT: the flagless registration-opt-in stores
 * (PWAStore, SignaturePadStore, CadStore, PdfEditorStore, ConversationStore)
 * report as `info` when they are left null — see `src/stores/featureStores.ts`.
 * At the library default that line goes nowhere, which is why the trap has
 * never been visible. `info` is only interesting while the app is coming up,
 * though, so it is captured during a boot window and then counted-only. An
 * admin can re-open the window at runtime from the diagnostics panel.
 */

/** How long after page load `info` is captured. */
const BOOT_WINDOW_MS = 20_000;

/** Bound on the boot log — it must not become a second unbounded buffer. */
const BOOT_LOG_CAPACITY = 200;

/** Console styling by level, dev only. */
const LEVEL_STYLE: Record<StoreDiagnosticLevel, string> = {
  debug: "color:#8a8a8a",
  info: "color:#0b6bcb",
  warn: "color:#9a6700",
  error: "color:#b3261e",
};

/**
 * Everything delivered while the boot window is open, at every level — the
 * first honest record of what the store layer says while HQ starts up.
 * Bounded; oldest entries are dropped once full.
 */
const bootLog: StoreDiagnosticEvent[] = [];
let bootLogDropped = 0;

let captureInfo = true;
let bootWindowOpen = true;
let bootWindowTimer: ReturnType<typeof setTimeout> | null = null;

/** Count of `info`/`debug` events seen after the boot window closed. */
let suppressedCount = 0;

function pushBootLog(event: StoreDiagnosticEvent): void {
  if (bootLog.length >= BOOT_LOG_CAPACITY) {
    bootLog.shift();
    bootLogDropped += 1;
  }
  bootLog.push(event);
}

function renderToConsole(event: StoreDiagnosticEvent): void {
  const head = `%c[diag:${event.level}] ${event.store} — ${event.message}`;
  const style = LEVEL_STYLE[event.level];
  const detail: Record<string, unknown> = { code: event.code };
  if (event.data !== undefined) detail.data = event.data;
  if (event.error !== undefined && event.error !== null) {
    detail.error = event.error;
  }
  if (event.level === "error") {
    console.error(head, style, detail);
  } else if (event.level === "warn") {
    console.warn(head, style, detail);
  } else {
    // `info`/`debug` only reach here while the boot window (or the admin
    // toggle) is open — the library never mirrors them itself.
    console.info(head, style, detail);
  }
}

/**
 * The `onEvent` handler. Receives already-redacted events at `info` and above.
 * Never throws (the sink would swallow it anyway, but a silent drop is worse
 * than a guarded no-op).
 */
function handleDiagnosticEvent(event: StoreDiagnosticEvent): void {
  try {
    if (bootWindowOpen) pushBootLog(event);

    const retained = hqDiagnosticsBuffer.retains(event.level);
    hqDiagnosticsBuffer.record(event);

    if (retained) {
      // warn/error: console in every mode — this is parity with the library's
      // own mirror, which HQ turned off, minus the unredacted payload.
      renderToConsole(event);
      return;
    }

    if (captureInfo || bootWindowOpen) {
      if (env.isDevelopment || env.enableDebugMode) renderToConsole(event);
      return;
    }
    suppressedCount += 1;
  } catch {
    // A diagnostics pipeline that can break the app is worse than no pipeline.
  }
}

/**
 * Emit into the SAME pipeline from code that has no RootStore.
 *
 * `api-client/apiClient.ts` is the reason this exists: it runs before any store
 * is constructed (it builds the socket, reads the stored tokens, and does the
 * pre-connect refresh), so it cannot reach `rootStore.diagnostics` — yet its
 * token-refresh failures are the closest thing HQ has to an early-warning for
 * the reconnect-auth hang that was only ever reported as "the screen is
 * frozen". Redacts exactly as the sink does, then goes through the same
 * routing, so an app-emitted event is indistinguishable from a library one in
 * the ring buffer.
 *
 * `debug` is dropped: the delivery floor is `info`, matching the sink.
 */
export function emitAppDiagnostic(
  level: StoreDiagnosticLevel,
  store: string,
  code: StoreDiagnosticCode,
  message: string,
  detail: { error?: unknown; data?: Record<string, unknown> } = {},
): void {
  if (level === "debug") return;
  handleDiagnosticEvent(
    defaultRedactDiagnostic({
      level,
      store,
      code,
      message,
      error: detail.error,
      data: detail.data,
      at: Date.now(),
    }),
  );
}

/**
 * Close the boot window. Called on a timer, and available to callers that know
 * boot is finished sooner.
 */
export function endBootWindow(): void {
  bootWindowOpen = false;
  captureInfo = false;
  if (bootWindowTimer !== null) {
    clearTimeout(bootWindowTimer);
    bootWindowTimer = null;
  }
}

/** Re-open (or close) `info` capture at runtime — the admin panel's toggle. */
export function setInfoCapture(enabled: boolean): void {
  captureInfo = enabled;
}

export function isInfoCaptureEnabled(): boolean {
  return captureInfo || bootWindowOpen;
}

/** The boot record, oldest first. A copy — callers must not mutate the log. */
export function getBootLog(): StoreDiagnosticEvent[] {
  return bootLog.slice();
}

export function getBootLogDroppedCount(): number {
  return bootLogDropped;
}

export function getSuppressedCount(): number {
  return suppressedCount;
}

/**
 * Human-readable dump of the boot record — what the sink saw while HQ started.
 * Used by the admin panel's copy button and by `window.__hqDiagnostics.dump()`.
 */
export function formatDiagnostics(events: StoreDiagnosticEvent[]): string {
  return events
    .map((e) => {
      const time = new Date(e.at).toISOString();
      const extra: string[] = [];
      if (e.data !== undefined) extra.push(`data=${JSON.stringify(e.data)}`);
      if (e.error !== undefined && e.error !== null) {
        extra.push(`error=${JSON.stringify(e.error)}`);
      }
      return `${time} ${e.level.toUpperCase().padEnd(5)} [${e.store}] ${
        e.message
      } (${e.code})${extra.length ? " " + extra.join(" ") : ""}`;
    })
    .join("\n");
}

/**
 * The block handed to `RootStoreProps.diagnostics`. `redact` is deliberately
 * NOT overridden: the library's default already reduces an Error to
 * `{name, message, code}` and scrubs credential-shaped keys, and HQ has no
 * app-specific secret shape the store layer would carry.
 */
export const hqDiagnosticsOptions: DiagnosticsOptions = {
  level: "info",
  console: false,
  onEvent: handleDiagnosticEvent,
};

/**
 * Start the boot window timer and expose the dev handle. Safe to call twice.
 * Called from `createRootStore` rather than at module scope so importing this
 * module from a test does not leave a timer running.
 */
export function startDiagnostics(): void {
  if (bootWindowTimer === null && bootWindowOpen) {
    bootWindowTimer = setTimeout(endBootWindow, BOOT_WINDOW_MS);
  }
  if (typeof window !== "undefined" && (env.isDevelopment || env.enableDebugMode)) {
    (window as unknown as Record<string, unknown>).__hqDiagnostics = {
      buffer: hqDiagnosticsBuffer,
      bootLog: getBootLog,
      dump: () => formatDiagnostics(getBootLog()),
      dumpRetained: () => formatDiagnostics(hqDiagnosticsBuffer.events),
      setInfoCapture,
      endBootWindow,
    };
  }
}
